import { pushToDataLayer } from './dataLayer';
import { matomoPush } from './matomo';

export class LoggingAnalyticsHandler {
  trackPageView(url) {
    console.log('Analytics page view:', url);
  }
}

// Google Tag Manager handler. Pushes a virtual_page_view to the dataLayer for
// in-app (SPA) navigation, so the GTM container can use a Custom Event trigger
// instead of the History Change trigger. GTM/GA4 auto-fires a page_view on the
// first hard load, so only subsequent navigations (previousPath set) are sent.
export class DataLayerAnalyticsHandler {
  trackPageView(canonicalPath, previousPath) {
    if (previousPath) {
      // page_location is captured synchronously (window.location is already
      // updated when this runs), so a rapid follow-up navigation can't leak in.
      // Only the title read is delayed: document.title updates after React
      // renders the new page (same reason GoogleAnalyticsHandler waits below).
      // page_referrer comes from the router's previous canonical path, since
      // document.referrer doesn't update in SPAs.
      const pageLocation = window.location.href;
      const pageReferrer = `${window.location.origin}${previousPath}`;
      window.setTimeout(() => {
        pushToDataLayer({
          event: 'virtual_page_view',
          page_location: pageLocation,
          page_title: document.title,
          page_referrer: pageReferrer,
        });
      }, 300);
    }
  }
}

// Matomo handler. The tracker snippet in util/includeScripts.js records the initial
// hard load, so only in-app (SPA) navigations (previousPath set) are sent here.
// https://developer.matomo.org/guides/spa-tracking
export class MatomoAnalyticsHandler {
  trackPageView(canonicalPath, previousPath) {
    if (previousPath) {
      const pageUrl = window.location.href;
      const referrerUrl = `${window.location.origin}${previousPath}`;
      // Point the tracker at the new URL right away, so events and site searches
      // fired while the page is still rendering are attributed to it. The page
      // view waits for React to render the new <title>, and re-sets the URL in
      // case a faster follow-up navigation has moved the tracker on meanwhile.
      matomoPush('setCustomUrl', pageUrl);
      window.setTimeout(() => {
        matomoPush('setReferrerUrl', referrerUrl);
        matomoPush('setCustomUrl', pageUrl);
        matomoPush('setDocumentTitle', document.title);
        matomoPush('trackPageView');
      }, 300);
    }
  }
}

// Google Analytics 4 (GA4) using gtag.js script, which is included in util/includeScripts.js
export class GoogleAnalyticsHandler {
  trackPageView(canonicalPath, previousPath) {
    // GA4 property. Manually send page_view events
    // https://developers.google.com/analytics/devguides/collection/gtagjs/single-page-applications
    // Note 1: You should turn "Enhanced measurement" off.
    //         It attaches own listeners to elements and that breaks in-app navigation.
    // Note 2: If previousPath is null (just after page load), gtag script sends page_view event automatically.
    //         Only in-app navigation needs to be sent manually from SPA.
    // Note 3: Timeout is needed because gtag script picks up <title>,
    //         and location change event happens before initial rendering.
    if (previousPath && window.gtag) {
      window.setTimeout(() => {
        window.gtag('event', 'page_view', {
          page_path: canonicalPath,
        });
      }, 300);
    }
  }
  trackCustomEvent(action) {
    if (window.gtag) {
      window.gtag('event', action);
    }
  }
  trackCustomEventWithProperties(action, properties) {
    if (window.gtag) {
      window.gtag('event', action, properties);
    }
  }
}
